import ExcelJS from 'exceljs';
import iconv from 'iconv-lite';
import {
  Result,
  Header,
  Nuclide,
  DEFAULT_ESTIMATION_UNCERTAINTY,
  DEFAULT_BEST_ACTIVITY,
  DEFAULT_NUCLIDE_DL,
  DEFAULT_DATE,
  DEFAULT_NAME,
  DEFAULT_ACTIVE_TIME,
  DEFAULT_DEAD_TIME,
  DEFAULT_SIZE,
  DEFAULT_SIZE_UNIT
} from './models';
import { getValueFromLine, getDateFromLine } from './dataHelper';
import { exportDataToExcel } from './exportation';

const TITLE_RAW_REGEX = /^\*{2,5}[^*]+\*{2,5}$/gm;
const TITLE_REGEX = /[A-Z0-9']+.*[A-Z0-9']+/g;

/**
 * Generate an XLSX file aggregating content of all RPT files
 */
export async function mergeG2000Results(files: File[]): Promise<ExcelJS.Buffer> {
  const results: Result[] = [];
  for (const file of files) {
    await file.arrayBuffer().then((fileContent) => {
      const decodedContent = iconv.decode(Buffer.from(fileContent), 'win1252');
      const res = extractDataFromContent(decodedContent);
      res.fileName = file.name;
      results.push(res);
    });
  }
  return exportDataToExcel(results);
}

/* "RAPPORT DE L’ANALYSE DU SPECTRE" */
export function extractHeader(lines: string[]): Header {
  let sampleName = DEFAULT_NAME;
  let measureDate = DEFAULT_DATE;
  let activeTime = DEFAULT_ACTIVE_TIME;
  let deadTime = DEFAULT_DEAD_TIME;
  let size = DEFAULT_SIZE;
  let sizeUnit = DEFAULT_SIZE_UNIT;

  lines.forEach((line) => {
    if (line.includes('Nom de l’échantillon')) {
      sampleName = getValueFromLine(line);
    } else if (line.includes('Date de mesure')) {
      measureDate = getDateFromLine(line);
    } else if (line.includes('Temps actif')) {
      const activeTimeString = getValueFromLine(line);
      // "3600.0 secondes" => 3600
      activeTime = Number(activeTimeString.split(' ')[0]);
    } else if (line.includes('Temps mort')) {
      const deadTimeString = getValueFromLine(line);
      // "1.73 %" => 1.73
      deadTime = Number(deadTimeString.split(' ')[0]);
    } else if (line.includes('Quantité d’échantillon')) {
      const fullSizeData = getValueFromLine(line);
      // "1.0000E+00 Unit " => ["1.0000E+00", "Unit"]
      const arrSize = fullSizeData.split(' ');
      if (arrSize.length === 2) {
        size = Number(arrSize[0]);
        sizeUnit = arrSize[1];
      }
    }
  });

  const header: Header = {
    sampleName,
    measureDate,
    activeTime,
    deadTime,
    size,
    sizeUnit
  };

  return header;
}

/* "RAPPORT LIMITES DE DETECTION" */
export function extractNuclideDL(lines: string[]): Map<string, Nuclide> {
  let hasConfidenceFactor = false;
  let isTable = false;
  let skipNextLine = false;
  const nuclideDLMap: Map<string, Nuclide> = new Map();

  lines.forEach((line) => {
    if (skipNextLine) {
      skipNextLine = false;
      return;
    }

    if (line.includes('Facteurs de confiance')) {
      hasConfidenceFactor = true;
    }

    // Here we suppose that the confidence factor attribute precedes the table
    if (!hasConfidenceFactor) return;

    if (line.includes('(keV)')) {
      isTable = true;
      skipNextLine = true;
      return;
    }

    if (line.includes('Page') || line.length <= 2) {
      isTable = false;
      return;
    }

    if (!isTable) return;

    // From here, all lines are rows of the table
    const columns: string[] = line.substring(10).split(/\s{2,}/); // minimum 2 spaces between columns because sometimes there is "Non Calc" as value
    const nuclideName: string = columns[0];
    const nuclideDL = Number(columns[4]);

    if (!nuclideName) return;

    nuclideDLMap.set(nuclideName, {
      nuclideName: nuclideName,
      nuclideDL: nuclideDL,
      bestActivity: -1,
      estimationUncertainty: -1
    });
  });

  return nuclideDLMap;
}

/* "RAPPORT LIMITES DE DETECTION  ISO 11929" */
export function extractNuclideActivity(lines: string[]): Map<string, Nuclide> {
  let hasConfidenceFactor = false;
  let isTable = false;
  let skipNextLine = false;
  const nuclideActivityMap: Map<string, Nuclide> = new Map<string, Nuclide>();

  lines.forEach((line) => {
    if (skipNextLine) {
      skipNextLine = false;
      return;
    }

    if (line.includes('Facteurs de confiance')) {
      hasConfidenceFactor = true;
    }

    // Here we suppose that the confidence factor attribute precedes the table
    if (hasConfidenceFactor) return;

    if (line.includes('Incert.')) {
      isTable = true;
      skipNextLine = true;
      return;
    }

    if (line.includes('Nucléide') || line.length <= 2) {
      isTable = false;
      return;
    }

    if (!isTable) return;

    // From here, all lines are rows of the table
    const columns = line.substring(5).split(/\s+/);

    /* /!\ When splitting with substring 5, there is a space before the nuclideName
		So there is an empty element on columns[0] thats why columns start at 1 */
    const nuclideName = columns[1].trim();
    const bestActivity = Number(columns[8].trim());
    const estimationUncertainty = Number(columns[9].trim());

    nuclideActivityMap.set(nuclideName, {
      nuclideName,
      nuclideDL: DEFAULT_NUCLIDE_DL,
      bestActivity,
      estimationUncertainty
    });
  });

  return nuclideActivityMap;
}

/* Main function */
function extractDataFromContent(fileContent: string): Result {
  let header: Header = {
    sampleName: DEFAULT_NAME,
    measureDate: DEFAULT_DATE,
    activeTime: DEFAULT_ACTIVE_TIME,
    deadTime: DEFAULT_DEAD_TIME,
    size: DEFAULT_SIZE,
    sizeUnit: DEFAULT_SIZE_UNIT
  };
  const nuclideDataMap = new Map<string, Nuclide>();
  const lines: string[] = fileContent.split('\n');

  // Loop variables
  let indexStart = 0;
  let indexEnd = 0;
  let previousTitle = '';

  lines.forEach((line, index) => {
    let newTitle: string;
    if (TITLE_RAW_REGEX.test(line)) {
      const matchResult = line.match(TITLE_REGEX);
      if (!matchResult) {
        return;
      }
      newTitle = matchResult[0];
      indexEnd = index - 1;

      switch (previousTitle) {
        case 'RAPPORT DE L’ANALYSE DU SPECTRE':
          header = extractHeader(lines.slice(indexStart, indexEnd));
          break;

        case 'RAPPORT LIMITES DE DETECTION': {
          const nuclideDLMap = extractNuclideDL(lines.slice(indexStart, indexEnd));
          addNuclideDLToDataMap(nuclideDLMap, nuclideDataMap);
          break;
        }

        case 'RAPPORT LIMITES DE DETECTION  ISO 11929': {
          const nuclideActivityMap = extractNuclideActivity(lines.slice(indexStart, indexEnd));
          addActivityToDataMap(nuclideActivityMap, nuclideDataMap);
          break;
        }
        default:
          break;
      }

      previousTitle = newTitle;
      indexStart = index + 1;
    } else if (index === lines.length - 1) {
      indexEnd = index;

      switch (previousTitle) {
        case 'RAPPORT DE L’ANALYSE DU SPECTRE':
          header = extractHeader(lines.slice(indexStart, indexEnd));
          break;

        case 'RAPPORT LIMITES DE DETECTION': {
          const nuclideDLMap: Map<string, Nuclide> = extractNuclideDL(lines.slice(indexStart, indexEnd));
          addNuclideDLToDataMap(nuclideDLMap, nuclideDataMap);
          break;
        }

        case 'RAPPORT LIMITES DE DETECTION  ISO 11929': {
          const nuclideActivityMap: Map<string, Nuclide> = extractNuclideActivity(lines.slice(indexStart, indexEnd));
          addActivityToDataMap(nuclideActivityMap, nuclideDataMap);
          break;
        }

        default:
          break;
      }
    }
  });

  const nuclideData: Nuclide[] = [...nuclideDataMap.values()];

  const result: Result = {
    header,
    nuclideData
  };

  return result;
}

function addNuclideDLToDataMap(nuclideDLMap: Map<string, Nuclide>, nuclideDataMap: Map<string, Nuclide>): void {
  nuclideDLMap.forEach((value, nuclideName) => {
    const existingNuclide: Nuclide | undefined = nuclideDataMap.get(nuclideName);
    nuclideDataMap.set(nuclideName, {
      nuclideName,
      nuclideDL: value.nuclideDL,
      bestActivity: existingNuclide ? existingNuclide.bestActivity : DEFAULT_BEST_ACTIVITY,
      estimationUncertainty: existingNuclide ? existingNuclide.estimationUncertainty : DEFAULT_ESTIMATION_UNCERTAINTY
    });
  });
}

function addActivityToDataMap(nuclideActivityMap: Map<string, Nuclide>, nuclideDataMap: Map<string, Nuclide>): void {
  nuclideActivityMap.forEach((value, nuclideName) => {
    const existingNuclide: Nuclide | undefined = nuclideDataMap.get(nuclideName);
    nuclideDataMap.set(nuclideName, {
      nuclideName,
      nuclideDL: existingNuclide ? existingNuclide.nuclideDL : DEFAULT_NUCLIDE_DL,
      bestActivity: value.bestActivity,
      estimationUncertainty: value.estimationUncertainty
    });
  });
}
